import lodashFind from "lodash/find";
import lodashGet from "lodash/get";
import lodashHas from "lodash/has";
import lodashFilter from "lodash/filter";
import lodashResult from "lodash/result";

import { httpGet, httpPut, httpPost, httpDelete } from "@/services/api.service";
import { ENDPOINT_NOTIFICATIONS_V2 } from "@/common/constants/api";
import { getFormatted, getFormattedDate } from "@/common/date.utilities";
import { upperCaseFirstLetter } from "@/common/basic.utilities";
import { DAYS_OF_WEEK } from "@/common/constants/misc";
import {
  NOTIFICATION_TRIPWIRE_TYPES,
  NOTIFICATION_DOC_TYPES,
  NOTIFICATION_BRIEFING_TYPES,
  NOTIFICATION_ASSET_TYPES,
  NOTIFICATION_TRIPWIRE_TYPE_RISK_RATING,
  NOTIFICATION_TRIPWIRE_TYPE_HOTSPOT,
  NOTIFICATION_TRIPWIRE_TYPE_PULSE,
  NOTIFICATION_EXPIRATION_ID_NEVER,
  NOTIFICATION_FREQUENCY_DAILY,
  NOTIFICATION_FREQUENCY_WEEKLY,
  NOTIFICATION_ASSET_RADIUS_DEFAULT,
  NOTIFICATION_ASSET_RADIUS_UNITS_DEFAULT,
} from "@/common/constants/notifications";
import { getTopicsForHotspots } from "@/services/events/topics.service";
import { getRiskCategoriesByGroup } from "@/services/controlRisks/risks.service";
import { getTargets } from "@/services/targets/target.service";
import { TARGETS_TYPES } from "@/common/constants/targets";
import {
  getAllCountries,
  getAllRegions,
  getAllCities,
  getAllCounties,
} from "@/common/maps/utilities";

export { _getNotifications as getNotifications };
export { _saveNotification as saveNotification };
export { _addNotification as addNotification };
export { _updateNotification as updateNotification };
export { _deleteNotification as deleteNotification };
export { _formatNotification as formatNotification };

let regionsAll;
let countriesAll;
let citiesAll;
let countiesAll;
let targetsAll;

const _getNotifications = async (params) => {
  try {
    const res = await httpGet({
      url: ENDPOINT_NOTIFICATIONS_V2,
      noCache: true,
    });
    let { data } = JSON.parse(res);

    let items = [];
    if (lodashGet(data, "searchesNotifications")) {
      await _formatNotifications(data.searchesNotifications);
      items = data.searchesNotifications;
    }

    const types = params.type ? params.type.map((o) => o.value) : null;
    if (params.type && params.type.length) {
      items = lodashFilter(items, (item) => {
        return types.includes(item.formatted.docType);
      });
    }

    const frequencies = params.frequency
      ? params.frequency.map((o) => o.value)
      : null;
    if (params.frequency && params.frequency.length) {
      items = lodashFilter(items, (item) => {
        return frequencies.includes(item.formatted.frequency);
      });
    }

    const shareds = params.shared ? params.shared.map((o) => o.value) : null;
    if (params.shared && params.shared.length) {
      items = lodashFilter(items, (item) => {
        const check = !!(
          item.formatted.sharedList && item.formatted.sharedList.length
        );
        return shareds.includes(check);
      });
    }

    const enableds = params.enabled ? params.enabled.map((o) => o.value) : null;
    if (params.enabled && params.enabled.length) {
      items = lodashFilter(items, (item) => {
        return enableds.includes(item.formatted.enabled);
      });
    }

    const deliveries = params.delivery
      ? params.delivery.map((o) => o.value)
      : null;
    if (params.delivery && params.delivery.length) {
      items = lodashFilter(items, (item) => {
        let includesATarget = false;
        if (item.formatted.useIntegrations) {
          TARGETS_TYPES.forEach((target) => {
            if (deliveries.includes(target.targetType)) {
              includesATarget = true;
            }
          });
        }

        return (
          (deliveries.includes("email") && item.formatted.useEmail) ||
          (deliveries.includes("push") && item.formatted.usePush) ||
          includesATarget
        );
      });
    }

    return items;
  } catch (error) {
    console.error(
      "Issue getting notifications in notifications.service.js",
      JSON.stringify(error)
    );
    return null;
  }
};

const _saveNotification = async (params) => {
  if (lodashHas(params, "formatted.id")) {
    return await _updateNotification(params);
  } else {
    return await _addNotification(params);
  }
};

const _addNotification = async (params) => {
  try {
    const notification = _formatNotificationForApi(params);
    let res = await httpPost({
      url: ENDPOINT_NOTIFICATIONS_V2,
      data: JSON.parse(JSON.stringify(notification)),
    });
    const { data } = JSON.parse(res);
    if (lodashGet(data, "searchesNotifications")) {
      return data.searchesNotifications;
    }
    return false;
  } catch (error) {
    console.error("Unable to create notification", error);
    return false;
  }
};

const _updateNotification = async (params) => {
  const endpoint = ENDPOINT_NOTIFICATIONS_V2 + "/" + params.formatted.id;
  try {
    const notification = _formatNotificationForApi(params);
    let res = await httpPut({
      url: endpoint,
      data: JSON.parse(JSON.stringify(notification)),
    });
    const { data } = JSON.parse(res);
    if (lodashGet(data, "searchesNotifications")) {
      return data.searchesNotifications;
    }
    return false;
  } catch (error) {
    console.error("Unable to update notification", error);
    return false;
  }
};

const _deleteNotification = async (params) => {
  const endpoint = ENDPOINT_NOTIFICATIONS_V2 + "/" + params.formatted.id;
  try {
    return await httpDelete({
      url: endpoint,
    });
  } catch (error) {
    console.error("Unable to delete notification", error);
    return false;
  }
};

const _formatNotificationForApi = (notification) => {
  const notif = {
    params: {},
  };
  if (notification) {
    if (lodashGet(notification, "params")) {
      notif.params = notification.params;
    }
    if (lodashGet(notification, "formatted.id")) {
      notif.id = notification.formatted.id;
    }
    if (lodashGet(notification, "formatted.docType")) {
      notif.docType = notification.formatted.docType;
    }
    if (lodashGet(notification, "formatted.name")) {
      notif.name = notification.formatted.name;
    }

    // tripwire props
    const locations = {};
    if (lodashGet(notification, "formatted.params.locations.regions.length")) {
      locations.regions = [];
      notification.formatted.params.locations.regions.forEach((o) => {
        locations.regions.push(o.value);
      });
    }
    if (
      lodashGet(notification, "formatted.params.locations.countries.length")
    ) {
      locations.countries = [];
      notification.formatted.params.locations.countries.forEach((o) => {
        locations.countries.push(o.value);
      });
    }
    if (lodashGet(notification, "formatted.params.locations.cities.length")) {
      locations.cities = [];
      notification.formatted.params.locations.cities.forEach((o) => {
        locations.cities.push(o.value);
      });
    }
    if (lodashGet(notification, "formatted.params.locations.counties.length")) {
      locations.counties = [];
      notification.formatted.params.locations.counties.forEach((o) => {
        locations.counties.push(o.value);
      });
    }
    if (
      lodashGet(locations, "regions.length") ||
      lodashGet(locations, "countries.length") ||
      lodashGet(locations, "cities.length") ||
      lodashGet(locations, "counties.length")
    ) {
      notif.params.locations = locations;
    }

    if (
      lodashGet(notification, "formatted.params.riskTypes.length") &&
      notification.formatted.params.riskTypes.length
    ) {
      notif.params.riskTypes = notification.formatted.params.riskTypes.map(
        (o) => o.value
      );
    }
    if (
      lodashGet(notification, "formatted.params.hotspotTypes.length") &&
      notification.formatted.params.hotspotTypes.length
    ) {
      notif.params.hotspotTypes = notification.formatted.params.hotspotTypes.map(
        (o) => o.value
      );
    }
    if (lodashGet(notification, "formatted.params.pulseTrigger")) {
      notif.params.pulseTrigger = notification.formatted.params.pulseTrigger;
      if (
        notification.formatted.params.pulseTrigger === "score-drop" &&
        lodashGet(notification, "formatted.params.pulseScoreDrop") &&
        !isNaN(notification.formatted.params.pulseScoreDrop)
      ) {
        notif.params.pulseScoreDrop = parseInt(
          notification.formatted.params.pulseScoreDrop
        );
      }
    }

    // asset props
    if (lodashGet(notification, "formatted.params.assetRadius")) {
      notif.params.assetRadius = notification.formatted.params.assetRadius;
      notif.params.assetRadiusUnits =
        notification.formatted.params.assetRadiusUnits;
    }
    if (lodashGet(notification, "formatted.params.managedAssets")) {
      notif.params.managedAssets = notification.formatted.params.managedAssets.map(
        (asset) => {
          let code = asset.code;
          if (!code && asset.country?.countryAlpha2Code) {
            code = asset.country?.countryAlpha2Code;
          }
          let lat = asset.lat;
          if (!lat && asset.latitude) {
            lat = asset.latitude;
          }
          let lng = asset.lng;
          if (!lng && asset.longitude) {
            lng = asset.longitude;
          }
          return {
            id: asset.id,
            lng: lng,
            lat: lat,
            code: code,
          };
        }
      );
    }

    // notification props
    notif.enabled = true;
    if (lodashHas(notification, "formatted.enabled")) {
      notif.enabled = !!notification.formatted.enabled;
    }
    if (lodashGet(notification, "formatted.frequency")) {
      notif.frequency = notification.formatted.frequency;
      if (
        notification.formatted.frequency === NOTIFICATION_FREQUENCY_DAILY ||
        notification.formatted.frequency === NOTIFICATION_FREQUENCY_WEEKLY
      ) {
        let schedule = notification.formatted.schedule;
        if (lodashGet(notification, "formatted.timeOfDay")) {
          schedule = notification.formatted.timeOfDay.replace(":", "_");
        }
        if (lodashGet(notification, "formatted.dayOfWeek")) {
          const dayOfWeek = lodashFind(DAYS_OF_WEEK, [
            "name",
            upperCaseFirstLetter(notification.formatted.dayOfWeek),
          ]);
          if (lodashGet(dayOfWeek, "abbrev2")) {
            schedule = `${dayOfWeek.abbrev2}_${schedule}`;
          }
        }
        if (schedule) {
          notif.schedule = schedule; // Example: "Mo_02_30"
        }
      }
      if (
        lodashGet(notification, "formatted.expiration") &&
        notification.formatted.expiration !== NOTIFICATION_EXPIRATION_ID_NEVER
      ) {
        notif.expiration = notification.formatted.expiration;
        if (lodashGet(notification, "formatted.expirationDate")) {
          notif.expirationDate = notification.formatted.expirationDate;
        }
      }
      if (lodashGet(notification, "formatted.delivery")) {
        notif.useEmail = notification.formatted.delivery.includes("email");
        notif.usePush = notification.formatted.delivery.includes("push");
        notif.useIntegrations = false;
      }
      if (
        lodashGet(notification, "formatted.targets") &&
        notification.formatted.targets.length
      ) {
        notif.useIntegrations = true;
        notif.integrationTargets = notification.formatted.targets;
      }
    }
    if (
      lodashGet(notification, "formatted.sharedList") &&
      notification.formatted.sharedList.length
    ) {
      notif.sharedList = notification.formatted.sharedList;
    }
    return notif;
  } else {
    return false;
  }
};

const _formatNotifications = async (notifications) => {
  // get all the regions, countries, cities, counties
  regionsAll = await getAllRegions();
  countriesAll = await getAllCountries();
  citiesAll = await getAllCities();
  countiesAll = await getAllCounties();
  targetsAll = await getTargets();

  const LEN = notifications.length;
  for (let i = 0; i < LEN; i++) {
    let notification = notifications[i];
    _formatNotification(notification);
  }

  // setting these to null so the browser doesn't have to hold on to them and take
  // up resources if need them again it should be able to grab them from cache anyway
  regionsAll = null;
  countriesAll = null;
  citiesAll = null;
  countiesAll = null;
  targetsAll = null;
};

const _formatNotification = async (notification) => {
  notification.formatted = {};

  notification.formatted.id = notification.searchNotificationId;
  notification.formatted.name = notification.name;

  const dTypes = NOTIFICATION_DOC_TYPES.map((o) => o.value);
  notification.formatted.docType = notification.docType;
  notification.formatted.docTypeName =
    notification.docType && dTypes.includes(notification.docType)
      ? lodashResult(
          lodashFind(NOTIFICATION_DOC_TYPES, ["value", notification.docType]),
          "text"
        )
      : "Unknown type";

  notification.formatted.creationTime = notification.creationTime
    ? getFormatted(notification.creationTime)
    : null;
  notification.formatted.lastUpdateTime = notification.lastUpdateTime
    ? getFormatted(notification.lastUpdateTime)
    : notification.formatted.creationTime;
  notification.formatted.isOwner =
    notification.userId &&
    notification.ownerId &&
    notification.userId === notification.ownerId;

  if (lodashGet(notification, "params")) {
    notification.formatted.params = {};

    // tripwire props
    let xTypes = NOTIFICATION_TRIPWIRE_TYPES;
    xTypes = xTypes.concat(NOTIFICATION_BRIEFING_TYPES);
    const tTypes = xTypes.map((o) => o.value);
    if (tTypes.includes(notification.docType)) {
      notification.formatted.params.locations = null;
      if (
        lodashGet(notification, "params.locations.regions.length") ||
        lodashGet(notification, "params.locations.countries.length") ||
        lodashGet(notification, "params.locations.cities.length") ||
        lodashGet(notification, "params.locations.counties.length")
      ) {
        const locations = {
          regions: [],
          countries: [],
          cities: [],
          counties: [],
        };
        if (lodashGet(notification, "params.locations.regions.length")) {
          notification.params.locations.regions.forEach((id) => {
            const region = lodashFind(regionsAll, ["properties.code", id]);
            if (region) {
              locations.regions.push({
                text: region.properties.name,
                value: region.properties.code,
              });
            }
          });
        }
        if (lodashGet(notification, "params.locations.countries.length")) {
          notification.params.locations.countries.forEach((id) => {
            const country = lodashFind(countriesAll, ["properties.iso2", id]);
            if (country) {
              locations.countries.push({
                text: country.properties.displayName,
                value: country.properties.iso2,
              });
            }
          });
        }
        if (lodashGet(notification, "params.locations.cities.length")) {
          notification.params.locations.cities.forEach((id) => {
            const city = lodashFind(citiesAll, ["properties.code", id]);
            if (city) {
              locations.cities.push({
                text: city.properties.displayName,
                value: city.properties.code,
              });
            }
          });
        }
        if (lodashGet(notification, "params.locations.counties.length")) {
          notification.params.locations.counties.forEach((id) => {
            const county = lodashFind(countiesAll, ["properties.code", id]);
            if (county) {
              locations.counties.push({
                text: county.properties.displayName,
                value: county.properties.code,
              });
            }
          });
        }
        notification.formatted.params.locations = locations;
      }

      if (notification.docType === NOTIFICATION_TRIPWIRE_TYPE_RISK_RATING) {
        notification.formatted.params.riskTypes = null;
        if (lodashGet(notification, "params.riskTypes.length")) {
          const allRiskTypes = await getRiskCategoriesByGroup({ ui: true });
          if (allRiskTypes) {
            const riskTypes = [];
            notification.params.riskTypes.forEach((type) => {
              allRiskTypes.forEach((risk) => {
                if (risk.name.toLowerCase() === type.toLowerCase()) {
                  riskTypes.push({
                    text: risk.display,
                    value: risk.name,
                  });
                }
              });
            });
            if (riskTypes.length) {
              notification.formatted.params.riskTypes = riskTypes;
            }
          }
        }
      }
      if (notification.docType === NOTIFICATION_TRIPWIRE_TYPE_HOTSPOT) {
        notification.formatted.params.hotspotTypes = null;
        if (lodashGet(notification, "params.hotspotTypes.length")) {
          const topics = getTopicsForHotspots();
          if (topics) {
            const hotspotTypes = [];
            notification.params.hotspotTypes.forEach((type) => {
              topics.forEach((topic) => {
                if (topic.apiName.toLowerCase() === type.toLowerCase()) {
                  hotspotTypes.push({
                    text: topic.singular,
                    value: topic.apiName,
                  });
                }
              });
            });
            if (hotspotTypes.length) {
              notification.formatted.params.hotspotTypes = hotspotTypes;
            }
          }
        }
      }
      if (notification.docType === NOTIFICATION_TRIPWIRE_TYPE_PULSE) {
        notification.formatted.params.pulseTrigger =
          notification.params.pulseTrigger || null;
        if (notification.params.pulseTrigger === "score-drop") {
          notification.formatted.params.pulseScoreDrop =
            notification.params.pulseScoreDrop || null;
        }
      }
    }

    // asset props
    const aTypes = NOTIFICATION_ASSET_TYPES.map((o) => o.value);
    if (aTypes.includes(notification.docType)) {
      notification.formatted.params.assetRadius =
        notification.params.assetRadius || NOTIFICATION_ASSET_RADIUS_DEFAULT;
      notification.formatted.params.assetRadiusUnits =
        notification.params.assetRadiusUnits ||
        NOTIFICATION_ASSET_RADIUS_UNITS_DEFAULT;
      notification.formatted.params.filters =
        notification.params.filters || null;
      notification.formatted.params.managedAssets = null;
      if (lodashGet(notification, "params.managedAssets.length")) {
        notification.formatted.params.managedAssets =
          notification.params.managedAssets;
      }
    }
  }

  // notification props
  notification.formatted.enabled = notification.enabled || false;
  notification.formatted.useEmail = notification.useEmail || false;
  notification.formatted.usePush = notification.usePush || false;
  notification.formatted.useIntegrations =
    notification.useIntegrations || false;

  if (notification.frequency) {
    notification.formatted.frequency = notification.frequency;
  }
  if (notification.schedule) {
    const parts = notification.schedule.split("_");
    let timeOfDay = null;
    if (parts && Array.isArray(parts) && parts.length > 0) {
      const dayOfWeekAbbr = parts.length > 2 ? parts[0] : null;
      if (dayOfWeekAbbr) {
        const dayOfWeek = lodashFind(DAYS_OF_WEEK, ["abbrev2", dayOfWeekAbbr]);
        if (dayOfWeek && dayOfWeek.name) {
          notification.formatted.dayOfWeek = dayOfWeek.name;
        }
      }

      if (parts.length > 2) {
        timeOfDay = `${parts[1]}:${parts[2]}`;
      } else if (parts.length < 2) {
        timeOfDay = `${parts[0]}:00`;
      } else {
        timeOfDay = `${parts[0]}:${parts[1]}`;
      }
      notification.formatted.timeOfDay = timeOfDay;
    }
    notification.formatted.scheduleInfo = "";
    if (notification.formatted.dayOfWeek) {
      notification.formatted.scheduleInfo = `${notification.formatted.dayOfWeek} `;
    }
    if (timeOfDay) {
      notification.formatted.scheduleInfo += timeOfDay;
    }
  }

  let expiration = notification.expiration;
  if (!expiration) {
    expiration = NOTIFICATION_EXPIRATION_ID_NEVER;
  }
  notification.formatted.expiration = expiration;
  notification.formatted.expirationDate = notification.expirationDate
    ? getFormattedDate(notification.expirationDate)
    : null;

  const delivery = [];
  if (notification.formatted.useEmail) {
    delivery.push("email");
  }
  if (notification.formatted.usePush) {
    delivery.push("push");
  }
  if (notification.formatted.useIntegrations) {
    delivery.push("target");
  }
  notification.formatted.delivery = delivery;

  notification.formatted.targets = notification.integrationTargets || [];
  notification.formatted.targetsArr = null;
  if (
    notification.integrationTargets &&
    notification.integrationTargets.length &&
    targetsAll &&
    targetsAll.length
  ) {
    const targetsArr = [];
    notification.integrationTargets.forEach((t) => {
      const target = lodashFind(targetsAll, ["targetId", t]);
      if (target && target.targetType) {
        for (let i = 0; i < TARGETS_TYPES.length; i++) {
          const tType = TARGETS_TYPES[i];
          if (tType.targetType === target.targetType) {
            if (tType.icon) {
              target.icon = tType.icon;
            }
            target.display = tType.display;
            targetsArr.push(target);
            break;
          }
        }
      }
    });
    notification.formatted.targetsArr = targetsArr;
  }

  notification.formatted.isShared = !!(
    notification.sharedList && notification.sharedList.length
  );
  notification.formatted.sharedList = notification.sharedList || [];
  notification.formatted.sharedInfo = null;
  if (notification.sharedList && notification.sharedList.length) {
    let sharedInfo = "Shared with: ";
    if (notification.sharedList.length > 5) {
      sharedInfo += `${notification.sharedList.length} colleagues`;
    } else {
      notification.sharedList.forEach((user) => {
        if (lodashGet(user, "email")) {
          sharedInfo += `<br/>${user.email}`;
        } else {
          sharedInfo += `<br/>${user}`;
        }
      });
    }
    notification.formatted.sharedInfo = sharedInfo;
  }
};
